import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_debounced_function } from '@/hooks/debounce/use_debounced_function';
import { use_component_is_mounted_0 } from '@/hooks/is_mounted/use_component_is_mounted_0';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { FIELD_PRIMITIVE__seed } from '@shared/dto/generation/graph_invoke/_fields/_primitives/FIELD_PRIMITIVE__seed';
import { useRef, useState } from 'react';
import { G_LIST_ITEM__seed } from './G_LIST_ITEM__seed';

interface PROPS {
  field: FIELD_PRIMITIVE__seed;
}

export function G_LIST__seed({ field }: PROPS) {
  /*
   * FIELDS
   */

  const { generation_current } = use_slice_generation(['generation_current']);

  const { g_hovered_grid2d_column, g_hovered_grid2d_row, g_hovered_grid2d_dimension_from_grid } = use_slice_ui([
    'g_hovered_grid2d_column',
    'g_hovered_grid2d_row',
    'g_hovered_grid2d_dimension_from_grid',
  ]);

  const { is_all_data_loaded } = use_slice_workspace(['is_all_data_loaded']);

  const ref_items_map = useRef<Map<number, HTMLDivElement>>(new Map());

  const { is_mounted } = use_component_is_mounted_0(on_mounted);

  const [is_scrolled_after_mount, set_is_scrolled_after_mount] = useState(false);

  const scroll_list_debounced = use_debounced_function(scroll_list, CONSTANTS_TIME__client.DEBOUNCE_GENERATION_GRID2D_AND_LIST);

  /*
   * EFFECTS
   */

  use_effect(() => scroll_list('instant'), [is_mounted, is_all_data_loaded, generation_current]);
  use_effect(() => scroll_list_debounced('smooth'), [g_hovered_grid2d_column, g_hovered_grid2d_row, field, g_hovered_grid2d_dimension_from_grid]);

  /*
   * EVENTS
   */
  function on_mounted() {
    scroll_list_debounced('instant');
  }

  /*
   * METHODS
   */

  function scroll_list(behavior: 'smooth' | 'instant') {
    if (!is_mounted) return;
    if (!is_all_data_loaded) return;
    if (generation_current.is_stub()) return;
    if (is_scrolled_after_mount) return;

    set_is_scrolled_after_mount(true);

    let item_hovered: number;

    if (g_hovered_grid2d_column.meta.field_id === field.meta.field_id) {
      assert_instanceof(g_hovered_grid2d_column, FIELD_PRIMITIVE__seed);
      item_hovered = g_hovered_grid2d_column.get_value();
    } else if (g_hovered_grid2d_row.meta.field_id === field.meta.field_id) {
      assert_instanceof(g_hovered_grid2d_row, FIELD_PRIMITIVE__seed);
      item_hovered = g_hovered_grid2d_row.get_value();
    } else {
      item_hovered = field.get_value();
    }

    if (g_hovered_grid2d_dimension_from_grid || item_hovered === field.get_value()) {
      const ref = ref_items_map.current.get_or_assert(item_hovered);
      ref.scrollIntoView({ behavior, block: 'center' });
    }
  }

  function set_ref(value: number, element: HTMLDivElement | null) {
    if (!element) return;

    ref_items_map.current.set(value, element);
  }

  function get_seeds_as_two_columns() {
    const rows: number[][] = [];

    let seeds = field.get_value_range();

    for (let i = 0; i < seeds.length; i += 2) {
      const left = seeds[i];
      const right = seeds[i + 1] ?? NaN;
      rows.push([left, right]);
    }

    return rows;
  }

  /*
   * RENDERS
   */

  function render_item(value: number, colorize_bg: boolean) {
    if (isNaN(value)) return <></>;

    let field_clone = field.clone();
    field_clone.set_value(value);

    return (
      <G_LIST_ITEM__seed ref={element => set_ref(value, element)} key={`${field.meta.field_id}_${value}`} field={field_clone} {...{ value, colorize_bg }} />
    );
  }

  function render_items_row() {
    let out: JSX.Element[] = [];

    for (let [index, row] of get_seeds_as_two_columns().entries()) {
      let value_left = row[0];
      let value_right = row[1];

      let colorize_left = index % 2 == 1;

      out.push(
        <div className="flex flex-row" key={`seed_${value_left}_${value_right}`}>
          {render_item(value_left, colorize_left)}
          <span className="w-0 h-full border-r border-component-border border-dashed px-0.5" />
          {render_item(value_right, !colorize_left)}
        </div>,
      );
    }

    return out;
  }

  /*
   * HTML
   */

  return (
    <div className="flex-1">
      <div className={`g_sidebar_settings_list_container max-h-48`}>{render_items_row()}</div>
    </div>
  );
}
